import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components/macro";
import { FlexColumn, FlexItem } from '@components/layout/FlexStyles';
import { useNavigate } from "react-router-dom";
import { ResponseT } from "@utils/api/base";
import useOrders from "./useOrders";
import { Order } from "../../type";
import PagePanel from "../pages/PagePanel";
import { Button } from "@mui/material";

const debug = false;
const OrdersDetails = (hash) => {
  const { getOrder } = useOrders();

  const navigate = useNavigate();

  // ###########################################################################
  // DATA LOADING
  // ###########################################################################
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = React.useCallback(async () => {
    debug && console.log("loadData");
    setLoading(true);
    let res: ResponseT<Order>;

    res = await getOrder(hash.hash);

    setData(res);
    setLoading(false);
    // setLoadedAll(!res.continues);
  }, []);

  useEffect(() => {
    if (loading === false) {
      loadData().then();
    }
  }, [loadData]);

  // ###########################################################################
  // Interactions
  // ###########################################################################

  return (
    <Container>
      {data != null && !loading ? (
        <Content>
          <Title>Order Details</Title>
          <PagePanel>
            <Row>
              <ItemTitle width="120px">Number:</ItemTitle>
              <Item width="400px">{data.number}</Item>
              <ItemTitle width="120px">Date:</ItemTitle>
              <Item width="200px">
                {new Date(data.orderDate).toLocaleDateString()}{" "}
                {new Date(data.orderDate).toLocaleTimeString()}
              </Item>
            </Row>
          </PagePanel>
          <PagePanel>
            <Row>
              <ItemTitle width="200px">Credits:</ItemTitle>
              <Item width="200px">{data.credits}</Item>
              <ItemTitle width="200px">Value:</ItemTitle>
              <Item width="200px">{data.value}</Item>
            </Row>
          </PagePanel>
          <StyledButton variant="outlined" onClick={() => navigate(`/orders`)}>
            Back to list
          </StyledButton>
        </Content>
      ) : (
        <>
          {loading ? (
            <Loader />
          ) : (
            <FlexColumn width={'100%'} h_centered>
              <FlexItem width={'80%'}>
                <Error>
                  Unable to locate any order for Id {hash.hash}. If you were directed to this page and think that this is an
                  error, please submit a bug report with the above hash Id.
                </Error>
              </FlexItem>
              <FlexItem>
                <StyledButton variant="outlined" onClick={() => navigate(`/orders`)}>
                  Back to list
                </StyledButton>
              </FlexItem>
            </FlexColumn>
          )}
        </>
      )}
    </Container>
  );
};

export default OrdersDetails;

interface StyledExtraProps {
  small?: boolean;
}

const Container = styled.div`
  height: calc(100vh - 100px);
  width: calc(100vw - 58px);
  padding-left: 5px;
  background-color: ${(p) => p.theme.palette.backgroundPrimary};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Error = styled.div`
  padding: 10px;
  color: ${(p) => p.theme.palette.titleSecondary};
  font-size: calc(0.8 * ${(props) => props.theme.sizes.default});
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px;
`;

const Title = styled.div`
  padding: 0 50px 5px 5px;
  font-size: ${(props) => props.theme.sizes.default};
  color: ${(props) => props.theme.palette.accentPrimary};
`;

const ItemTitle = styled.div<{ width?: string }>`
  display: flex;
  margin: 5px;
  font-size: ${(props) => props.theme.sizes.small};
  color: ${(props) => props.theme.palette.textPrimary};
  font-weight: bold;
  ${(p) => p.width && `width: ${p.width};`}
`;

const Item = styled.div<{ width?: string }>`
  display: flex;
  margin: 5px;
  font-size: ${(props) => props.theme.sizes.small};
  color: ${(props) => props.theme.palette.textPrimary};
  ${(p) => p.width && `min-width: ${p.width};`}
`;

const rotate = keyframes`
          from {
            transform: rotate(0deg);
          }
        
          to {
            transform: rotate(360deg);
          }
        `;

const Loader = styled.div<StyledExtraProps>`
  display: inline-block;
  border-radius: 100%;
  margin: 2px;
  border: ${(p) => p.theme.borders.default};
  border-bottom-color: transparent;
  width: ${(props) => (props.small ? 12 : 22)}px;
  height: ${(props) => (props.small ? 12 : 22)}px;
  animation: ${rotate} 0.75s linear infinite;
  background-color: ${(p) => p.theme.palette.backgroundPrimary};
`;

const StyledButton = styled(Button)`
  position: relative;
  float: right;
  && {
    background-color: ${(p) => p.theme.palette.backgroundSecondary};
    padding: 10px 15px;
    font-size: ${(props) => props.theme.sizes.small};
    margin-top: 20px;
  }
`;
